import { useQuery } from "@tanstack/react-query";
import {
  Linkedin,
  Mail,
  ArrowUpRight,
  Youtube,
  Rss,
  Link2,
  CalendarDays,
  Instagram,
  Facebook,
  BookOpen,
  Bell,
  type LucideIcon,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { getLinkSet, getText } from "@/lib/site-content.functions";
import { XIcon } from "@/components/XIcon";
import { BlueskyIcon } from "@/components/BlueskyIcon";

export type LinkItem = {
  id?: string;
  item_key: string;
  label: string;
  href: string;
  sub?: string | null;
  icon?: string | null;
  color_var?: string | null;
  external: boolean;
  enabled: boolean;
  position: number;
};

type AnyIcon = LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;

export const ICONS: Record<string, AnyIcon> = {
  Linkedin,
  Mail,
  ArrowUpRight,
  Youtube,
  Rss,
  Link2,
  CalendarDays,
  Instagram,
  Facebook,
  BookOpen,
  Bell,
  XIcon,
  BlueskyIcon,
};

export const ICON_NAMES = Object.keys(ICONS);

export function getIcon(name?: string | null): AnyIcon | null {
  if (!name) return null;
  return ICONS[name] ?? null;
}

/** Hardcoded fallback so SSR + initial paint match the DB seed and never flicker. */
export const FALLBACK_LINK_SETS: Record<string, LinkItem[]> = {
  header_nav: [
    { item_key: "home", label: "Home", href: "/", external: false, enabled: true, position: 0 },
    { item_key: "work", label: "Work", href: "/work", external: false, enabled: true, position: 1 },
    { item_key: "services", label: "Services", href: "/services", external: false, enabled: true, position: 2 },
    { item_key: "tools", label: "Tools", href: "/tools", external: false, enabled: true, position: 3 },
    { item_key: "media", label: "Media", href: "/media", external: false, enabled: true, position: 4 },
    { item_key: "about", label: "About", href: "/about", external: false, enabled: true, position: 5 },
    { item_key: "blog", label: "Blog", href: "/blog", external: false, enabled: true, position: 6 },
  ],
  footer_nav: [
    { item_key: "home", label: "Home", href: "/", external: false, enabled: true, position: 0 },
    { item_key: "work", label: "Work", href: "/work", external: false, enabled: true, position: 1 },
    { item_key: "services", label: "Services", href: "/services", external: false, enabled: true, position: 2 },
    { item_key: "about", label: "About", href: "/about", external: false, enabled: true, position: 3 },
    { item_key: "blog", label: "Blog", href: "/blog", external: false, enabled: true, position: 4 },
    { item_key: "newsletter", label: "Newsletter", href: "/newsletter", external: false, enabled: true, position: 5 },
    { item_key: "contact", label: "Contact", href: "/contact", external: false, enabled: true, position: 6 },
    { item_key: "resume", label: "Resume", href: "/resume", external: false, enabled: true, position: 7 },
  ],
  footer_connect: [
    { item_key: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/in/scottcbusiness/", icon: "Linkedin", external: true, enabled: true, position: 0 },
    { item_key: "x", label: "X", href: "https://x.com/scottcbusiness", icon: "XIcon", external: true, enabled: true, position: 1 },
    { item_key: "youtube", label: "YouTube", href: "https://www.youtube.com/@scottcbusiness", icon: "Youtube", external: true, enabled: true, position: 2 },
    { item_key: "bluesky", label: "Bluesky", href: "https://bsky.app/profile/scottcbusiness.com", icon: "BlueskyIcon", external: true, enabled: true, position: 3 },
    { item_key: "email", label: "Email", href: "mailto:scottcbusiness@protonmail.com", icon: "Mail", external: true, enabled: true, position: 4 },
    { item_key: "calendly", label: "Book a meeting", href: "https://calendly.com/scottthomascunningham/30min", icon: "CalendarDays", external: true, enabled: true, position: 5 },
    { item_key: "all_links", label: "All Links", href: "/about#links", icon: "Link2", external: false, enabled: true, position: 6 },
    { item_key: "rss", label: "RSS", href: "/rss.xml", icon: "Rss", external: false, enabled: true, position: 7 },
  ],
  footer_legal: [
    { item_key: "terms", label: "Terms", href: "/terms", external: false, enabled: true, position: 0 },
    { item_key: "privacy", label: "Privacy", href: "/privacy", external: false, enabled: true, position: 1 },
  ],
  about_all_links: [
    { item_key: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/in/scottcbusiness/", sub: "Professional updates & posts", icon: "Linkedin", color_var: "var(--color-primary)", external: true, enabled: true, position: 0 },
    { item_key: "x", label: "X", href: "https://x.com/scottcbusiness", sub: "Tech commentary & updates", icon: "XIcon", color_var: "var(--color-electric)", external: true, enabled: true, position: 1 },
    { item_key: "instagram", label: "Instagram", href: "https://www.instagram.com/scottcbusiness/", sub: "Photos & short-form updates", icon: "Instagram", color_var: "var(--color-pink)", external: true, enabled: true, position: 2 },
    { item_key: "youtube", label: "YouTube", href: "https://www.youtube.com/@scottcbusiness", sub: "The Tech & Things Show", icon: "Youtube", color_var: "var(--color-pink)", external: true, enabled: true, position: 3 },
    { item_key: "bluesky", label: "Bluesky", href: "https://bsky.app/profile/scottcbusiness.com", sub: "Tech commentary & conversations", icon: "BlueskyIcon", color_var: "var(--color-violet)", external: true, enabled: true, position: 4 },
    { item_key: "facebook", label: "Facebook", href: "https://www.facebook.com/ScottCBusiness/", sub: "Professional updates & posts", icon: "Facebook", color_var: "var(--color-primary)", external: true, enabled: true, position: 5 },
    { item_key: "blog", label: "Blog", href: "/blog", sub: "Essays, notes & field reports", icon: "BookOpen", color_var: "var(--color-primary)", external: false, enabled: true, position: 6 },
    { item_key: "calendly", label: "Book a meeting", href: "https://calendly.com/scottthomascunningham/30min", sub: "30-min intro via Calendly", icon: "CalendarDays", color_var: "var(--color-violet)", external: true, enabled: true, position: 7 },
    { item_key: "email", label: "Email", href: "mailto:scottcbusiness@protonmail.com", sub: "scottcbusiness@protonmail.com", icon: "Mail", color_var: "var(--color-electric)", external: true, enabled: true, position: 8 },
    { item_key: "newsletter", label: "Newsletter", href: "/newsletter", sub: "The Digital Debrief, monthly", icon: "Mail", color_var: "var(--color-pink)", external: false, enabled: true, position: 9 },
    { item_key: "rss", label: "RSS", href: "/rss.xml", sub: "Subscribe in your reader", icon: "Rss", color_var: "var(--color-violet)", external: false, enabled: true, position: 10 },
    { item_key: "contact", label: "Contact", href: "/contact", sub: "Start a conversation", icon: "ArrowUpRight", color_var: "var(--color-foreground)", external: false, enabled: true, position: 11 },
  ],
};

export const FALLBACK_TEXT: Record<string, string> = {
  "home.hero.eyebrow": "Portfolio",
  "home.hero.name": "Scott Cunningham",
  "home.hero.subtitle": "Social media and content lead for tech brands",
  "home.hero.body":
    "I'm Scott Cunningham (@scottcbusiness), a marketer who lives at the intersection of content and technology. I help AI, SaaS, cloud, Web3, and cybersecurity brands turn complex ideas into clear, strategic messaging that grows audience, drives revenue, and goes well beyond vanity metrics.",
  "home.hero.pickPath": "Pick your path",
};

export const LINK_SET_LABELS: Record<string, string> = {
  header_nav: "Header navigation",
  footer_nav: "Footer — Navigation column",
  footer_connect: "Footer — Connect With Me",
  footer_legal: "Footer — Legal (bottom bar)",
  about_all_links: "About page — All my links",
};

export const LINK_SETS = Object.keys(LINK_SET_LABELS);

export function useLinkSet(setKey: string): LinkItem[] {
  const fallback = FALLBACK_LINK_SETS[setKey] ?? [];
  const { data } = useQuery({
    queryKey: ["content_link_sets", setKey],
    queryFn: async () => {
      const rows = await getLinkSet({ data: { setKey } });
      return (rows ?? []) as LinkItem[];
    },
    staleTime: 60_000,
    initialData: fallback,
  });
  return data && data.length ? data : fallback;
}

export function useText(key: string, fallback?: string): string {
  const initial = fallback ?? FALLBACK_TEXT[key] ?? "";
  const { data } = useQuery({
    queryKey: ["content_text", key],
    queryFn: async () => {
      const value = await getText({ data: { key } });
      return value ?? initial;
    },
    staleTime: 60_000,
    initialData: initial,
  });
  return data ?? initial;
}

export function isExternalHref(href: string): boolean {
  return href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:");
}